import React from "react";
import axios from "axios";
import SharedSiteDown from "./SharedSiteDown";

class SiteDownErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isApiDown: false,
      checkingApi: false,
    };
    this.handleRetry = this.handleRetry.bind(this);
    this.checkApiHealth = this.checkApiHealth.bind(this);
  }

  async checkApiHealth() {
    try {
      // Try to hit the health endpoint to check if API is down
      const response = await axios
        .get("/api/health", {
          timeout: 5000,
          validateStatus: () => true, // Accept any status code
        })
        .catch(async () => {
          // If health endpoint doesn't exist, try a simple GET to the API root
          return axios.get("/api/", {
            timeout: 5000,
            validateStatus: () => true,
          });
        });

      // If health endpoint returns 500/502/503/504, API is down
      if (
        response &&
        (response.status === 500 ||
          response.status === 502 ||
          response.status === 503 ||
          response.status === 504)
      ) {
        console.error(
          "Health endpoint returned error status:",
          response.status
        );
        return false; // API is down
      }

      // Check if the health endpoint indicates the service is degraded
      if (
        response &&
        response.data &&
        (response.data.status === "degraded" ||
          response.data.database === false)
      ) {
        return false; // API is down/degraded
      }

      // If we get a successful response, API is reachable
      return true;
    } catch (error) {
      // Network error - API is unreachable
      //console.error("API health check failed:", error);
      return false;
    }
  }

  static getDerivedStateFromError(error) {
    // Catch ALL errors and check if they might be API-related
    //console.error("Error caught by boundary:", error);

    return {
      hasError: true,
      error,
      checkingApi: true,
    };
  }

  async componentDidCatch(error, errorInfo) {
    console.error("Error details:", error, errorInfo);

    // Extract error details
    const errorMessage = (error && error.message) || "";
    //const errorString = error.toString() || "";

    // Check if this is an Axios error with response data
    //const isAxiosError = error && error.isAxiosError;
    const httpStatus = error && error.response && error.response.status;

    // ONLY show site down page for these specific conditions:
    // 1. Network errors (ERR_NETWORK) - server completely unreachable
    // 2. 502 Bad Gateway - reverse proxy can't reach backend
    // 3. 503 Service Unavailable - server explicitly down for maintenance
    // 4. 504 Gateway Timeout - backend not responding
    const isSiteDownError =
      errorMessage.includes("ERR_NETWORK") ||
      errorMessage.includes("Network Error") ||
      httpStatus === 502 ||
      httpStatus === 503 ||
      httpStatus === 504 ||
      errorMessage.includes("status code 502") ||
      errorMessage.includes("status code 503") ||
      errorMessage.includes("status code 504");

    // Do NOT show site down for:
    // - 4xx errors (400, 401, 403, 404, etc.) - these are client/request errors
    // - 500 errors - these are application bugs, not "site down"
    // - Regular React Query failures with valid API responses
    // - Any other application errors

    if (isSiteDownError) {
      // Only for actual site down errors, verify with health check
      this.setState({ checkingApi: true });
      const apiIsUp = await this.checkApiHealth();

      if (!apiIsUp) {
        // API is confirmed down, show site down page
        this.setState({
          isApiDown: true,
          checkingApi: false,
        });
      } else {
        // API is up, this was a transient error
        // Reset and let normal error handling take over
        this.setState({
          isApiDown: false,
          checkingApi: false,
          hasError: false,
        });

        //console.error("Transient network error (API is up):", error);
      }
    } else {
      // Not a site down error - could be validation error, 404, 500, etc.
      // Reset state and let normal error handling take over
      this.setState({
        hasError: false,
        checkingApi: false,
      });

      // Log for debugging but don't show site down page
      // console.error("Application error (not site down):", {
      //   message: errorMessage,
      //   status: httpStatus,
      //   isAxiosError,
      //   error,
      // });
    }
  }

  handleRetry() {
    this.setState({
      hasError: false,
      error: null,
      isApiDown: false,
      checkingApi: false,
    });
    // Force a page reload to retry
    window.location.reload();
  }

  render() {
    // Show loading while checking API
    if (this.state.checkingApi) {
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "100vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Checking service availability...</span>
          </div>
        </div>
      );
    }

    // Show site down component if API is confirmed down
    if (this.state.hasError && this.state.isApiDown) {
      return <SharedSiteDown onRetry={this.handleRetry} />;
    }

    // For all other cases, render children normally
    return this.props.children;
  }
}

export default SiteDownErrorBoundary;
